import React, { useState } from 'react';
import { dateToString, DATE_FORMAT_ONLY_MONTH_AND_YEAR } from '../utilities/formattingUtilities';
import { DoctorGroup } from './groupCandidatesByDoctor';
import './GroupedDoctorCard.scss';
import { formatLocation } from './helpers';
import SourcePill from './SourcePill';

const valueToDisplay = (value: string | undefined) =>
  value === undefined || value === '' ? '-' : value;

interface GroupedDoctorCardProps {
  group: DoctorGroup;
  /** Rank badge shown on the left (only when not selectable). */
  rank?: number;
  /** When true, a checkbox replaces the rank and clicking the header toggles selection. */
  selectable?: boolean;
  selected?: boolean;
  /** Called with the group when selected, or `null` when toggling off. */
  onSelect?: (group: DoctorGroup | null) => void;
}

const ICD_CHIP_PREVIEW = 3;

const GroupedDoctorCard = ({
  group,
  rank,
  selectable = false,
  selected = false,
  onSelect,
}: GroupedDoctorCardProps) => {
  const [open, setOpen] = useState(false);

  const locationLine = formatLocation(
    group.address,
    group.city,
    group.state,
    group.zipcode,
  );

  const icdCount = group.icds.length;
  const isExpandable = icdCount > 1;
  const showSingleIcd = icdCount === 1;
  const previewIcds = group.icds.slice(0, ICD_CHIP_PREVIEW);
  const alsoIn = group.alsoIn ?? [];

  const groupClass = [
    'grouped-doctor-card',
    selectable ? 'selectable' : '',
    selected ? 'selected' : '',
    open ? 'open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // In non-selectable mode (ApsCandidates), the whole header is the expand
  // toggle. In selectable mode (APS Order Details), the header toggles
  // selection and the chevron is the dedicated expand control.
  const headerIsExpandTrigger = !selectable && isExpandable;
  const headerClass = [
    'doctor-header',
    headerIsExpandTrigger ? 'expandable' : '',
    selectable ? 'selectable-header' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const chipClass = group.source === 'Rx' ? 'icd-chip rx-chip' : 'icd-chip';

  const handleHeaderClick = () => {
    if (selectable && onSelect) {
      onSelect(selected ? null : group);
    } else if (isExpandable) {
      setOpen(o => !o);
    }
  };

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleHeaderClick();
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(o => !o);
  };

  const headerInteractive = selectable || headerIsExpandTrigger;

  return (
    <div className={groupClass}>
      <div
        className={headerClass}
        onClick={headerInteractive ? handleHeaderClick : undefined}
        onKeyDown={headerInteractive ? handleHeaderKeyDown : undefined}
        role={headerInteractive ? 'button' : undefined}
        tabIndex={headerInteractive ? 0 : undefined}
        aria-pressed={selectable ? selected : undefined}
        aria-expanded={isExpandable ? open : undefined}
      >
        {selectable ? (
          <span
            className={`doctor-checkbox ${selected ? 'checked' : ''}`}
            aria-hidden
          />
        ) : (
          rank !== undefined && <div className="rank">{rank}</div>
        )}
        <div className="doctor-main">
          <div className="doctor-name">
            {valueToDisplay(group.name)}
            {group.source === 'PreviouslyOrdered' && (
              <SourcePill source={group.source} />
            )}
            {alsoIn.map(src => (
              <SourcePill key={src} source={src} prefix="Also in " />
            ))}
          </div>
          <div className="doctor-meta">
            {group.source !== 'PreviouslyOrdered' && (
              <SourcePill source={group.source} />
            )}
            {group.specialization && (
              <span className="spec-tag">{group.specialization}</span>
            )}
            {group.source !== 'PreviouslyOrdered' && (
              <>
                <span className="sep">·</span>
                <span className="info-pill">
                  Last visit {dateToString(group.lastVisitDate, DATE_FORMAT_ONLY_MONTH_AND_YEAR)}
                </span>
              </>
            )}
            {group.npi && (
              <>
                <span className="sep">·</span>
                <span className="info-pill">
                  NPI <span className="npi-value">{group.npi}</span>
                </span>
              </>
            )}
            {group.source !== 'PreviouslyOrdered' && group.information && (
              <>
                <span className="sep">·</span>
                <span className="info-pill">{group.information}</span>
              </>
            )}
            {group.phoneNumber && (
              <>
                <span className="sep">·</span>
                <span className="info-pill">{group.phoneNumber}</span>
              </>
            )}
            {locationLine && (
              <>
                <span className="sep">·</span>
                <span className="info-pill address">{locationLine}</span>
              </>
            )}
            {group.facilityName && (
              <>
                <span className="sep">·</span>
                <span className="info-pill">{group.facilityName}</span>
              </>
            )}
          </div>

          {showSingleIcd && (
            <div className="icd-single">
              {group.icds[0].code && (
                <span className={chipClass}>{group.icds[0].code}</span>
              )}
              {group.icds[0].description && (
                <span className="icd-desc">{group.icds[0].description}</span>
              )}
            </div>
          )}

          {isExpandable && (
            <div className="icd-summary">
              {previewIcds.map((icd, idx) => (
                <span key={`${icd.code}-${idx}`} className={chipClass}>
                  {icd.code || '—'}
                </span>
              ))}
              <span className="icd-more">
                {icdCount} conditions
                {selectable ? '' : ' · click to expand'}
              </span>
            </div>
          )}
        </div>
        {isExpandable && (
          <button
            type="button"
            className="expand-btn"
            onClick={handleExpandClick}
            aria-label={open ? 'Collapse conditions' : 'Expand conditions'}
            aria-expanded={open}
          >
            ▼
          </button>
        )}
      </div>

      {isExpandable && (
        <div className="icd-detail">
          <table className="icd-table">
            <thead>
              <tr>
                <th>ICD Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {group.icds.map((icd, idx) => (
                <tr key={`${icd.code}-${idx}`}>
                  <td>
                    <span className="icd-code">{icd.code || '—'}</span>
                  </td>
                  <td>{icd.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GroupedDoctorCard;
